import { useEffect, useState } from 'react';
import { clients } from '@ooh/contracts';
import type { Client, ListClientsQuery } from '@ooh/contracts';
import { useApiQuery } from '../data/use-api-query';
import { useHasPermission } from '../plugs/current-user';

function isClient(value: unknown): value is Client {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const candidate = value as { id?: unknown; name?: unknown };
  return typeof candidate.id === 'number' && typeof candidate.name === 'string';
}

function toClientArray(value: unknown): Client[] | null {
  return Array.isArray(value) && value.every(isClient) ? value : null;
}

export function normalizeClientList(data: Client[] | { data: Client[] } | undefined): Client[] {
  const directClients = toClientArray(data);
  if (directClients) {
    return directClients;
  }

  if (data && !Array.isArray(data)) {
    return toClientArray(data.data) ?? [];
  }

  return [];
}

export function useClientsList(query?: ListClientsQuery['q']) {
  const canViewClients = useHasPermission('client.view');
  return useApiQuery(clients.list, { q: query }, { enabled: canViewClients });
}

/** Minimum number of characters before issuing a suggestion query. */
export const SUGGESTION_MIN_CHARS = 2;

/**
 * Debounced client name suggestions for the create dialog autocomplete.
 *
 * Returns an array of matching active clients from `GET /clients?q=`.
 * Returns an empty array if the input is shorter than SUGGESTION_MIN_CHARS.
 */
export function useClientSuggestions(query: string): Client[] {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const canViewClients = useHasPermission('client.view');

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < SUGGESTION_MIN_CHARS) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data = [] } = useApiQuery(
    clients.list,
    { q: debouncedQuery || undefined },
    { enabled: canViewClients && debouncedQuery.length >= SUGGESTION_MIN_CHARS },
  );

  if (query.trim().length < SUGGESTION_MIN_CHARS || query.trim() !== debouncedQuery) return [];
  return data;
}
